import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useActivePolling } from '../hooks/useActivePolling';
import {
  BarChart2, Shield, Brain, TrendingUp, TrendingDown, AlertTriangle,
  RefreshCw, Loader2, Activity, Target, Zap, Eye, Grid3X3, Percent,
  Calculator, Sparkles, ChevronRight, ArrowUpRight, ArrowDownRight,
  Info, DollarSign, Layers, Clock, Crosshair, Lock, Globe, Gauge,
  PieChart, GitBranch, Anchor, Crown, ExternalLink, FlaskConical, Bot
} from 'lucide-react';
import { Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
import { InfoTip } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import {
  getRiskMetrics, getCorrelationMatrix, runStressTest,
  getFactorExposure, getKellyCriterion, getAiCommentary,
  getPairsTradingOpportunities, getYieldOpportunities,
  getWhaleAlerts, getSmartMoneyWallets, getAccumulationTrends
} from '../services/api';
import { BetaBadge, FeatureDisclaimerModal, PersistentDisclaimerBanner, BetaFeedbackButton, useBetaGate } from '../components/BetaFramework';
import { formatUsd, formatDateTime, formatTime } from '../services/displayPrefs';

// ─── Tab config ─────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Risk Overview', icon: Shield },
  { id: 'stress', label: 'Stress Test', icon: AlertTriangle },
  { id: 'correlation', label: 'Correlation', icon: Grid3X3 },
  { id: 'factors', label: 'Factor Exposure', icon: PieChart },
  { id: 'kelly', label: 'Kelly Criterion', icon: Calculator },
  { id: 'pairs', label: 'Pairs Trading', icon: GitBranch },
  { id: 'whales', label: 'Whale Watcher', icon: Eye },
  { id: 'yield', label: 'Yield', icon: DollarSign },
];

// ─── Helper Components ──────────────────────────────────────────────────────

function MetricCard({ label, value, benchmark, color, icon: Icon, subtext, info }) {
  return (
    <div className="bg-dark-700/30 rounded-xl p-4 border border-dark-600/30">
      <div className="flex items-center justify-between mb-1">
        <span className="text-gray-500 text-[10px] flex items-center gap-1">
          {Icon && <Icon className="w-3 h-3" />} {label}
          {info && <InfoTip side="bottom">{info}</InfoTip>}
        </span>
        {benchmark && <span className="text-[9px] text-gray-600">{benchmark}</span>}
      </div>
      <p className={`text-xl font-bold font-mono ${color || 'text-white'}`}>{value}</p>
      {subtext && <p className="text-[10px] text-gray-500 mt-0.5">{subtext}</p>}
    </div>
  );
}

function RatingBadge({ value, thresholds }) {
  const num = parseFloat(value);
  const color = num >= thresholds[2] ? 'text-emerald-400 bg-emerald-500/10' : num >= thresholds[1] ? 'text-amber-400 bg-amber-500/10' : 'text-red-400 bg-red-500/10';
  const label = num >= thresholds[2] ? 'Excellent' : num >= thresholds[1] ? 'Average' : 'Poor';
  return <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${color}`}>{label}</span>;
}

// ─── Empty / "needs real data" card ────────────────────────────────────────
// Shown on any tab where the backend reports dataSource === 'demo' or marks
// the response with requiresRealData/computationPending. Replaces the old
// seeded-demo metrics so no fabricated numbers reach the user.
function EmptyDemoCard({ icon: Icon, title, description, currentTrades, minRequired, hideActions = false }) {
  const progress = currentTrades != null && minRequired
    ? Math.min(100, (currentTrades / minRequired) * 100)
    : null;
  return (
    <div className="bg-dark-800 rounded-2xl border border-amber-500/20 p-10 text-center">
      <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-4">
        <Icon className="w-7 h-7 text-amber-400" />
      </div>
      <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
      <p className="text-gray-400 text-sm max-w-md mx-auto mb-6 leading-relaxed">{description}</p>
      {progress != null && (
        <div className="flex items-center justify-center gap-3 text-xs text-gray-500 mb-6">
          <span>{currentTrades} / {minRequired} trades</span>
          <div className="w-40 h-1.5 bg-dark-700 rounded-full overflow-hidden">
            <div className="h-full bg-amber-400 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}
      {!hideActions && (
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Link to="/paper-trading" className="inline-flex items-center gap-1.5 bg-amber-400 text-gray-900 hover:bg-amber-300 text-sm font-semibold px-4 py-2 rounded-xl transition-colors">
            <FlaskConical className="w-4 h-4" /> Start Paper Trading
          </Link>
          <Link to="/bots" className="inline-flex items-center gap-1.5 bg-dark-700 hover:bg-dark-600 border border-dark-600 text-gray-300 text-sm px-4 py-2 rounded-xl transition-colors">
            <Bot className="w-4 h-4" /> Set up a Bot
          </Link>
          <Link to="/swap" className="inline-flex items-center gap-1.5 bg-dark-700 hover:bg-dark-600 border border-dark-600 text-gray-300 text-sm px-4 py-2 rounded-xl transition-colors">
            <Zap className="w-4 h-4" /> DEX Swap
          </Link>
        </div>
      )}
    </div>
  );
}

function isLocked(data) {
  return data?.requiresRealData || data?.computationPending;
}

// Display labels + accent colors per trade source. Used by PerSourceBreakdown.
const SOURCE_META = {
  smart_trade:  { label: 'Smart Trade',  icon: Target,    color: 'text-amber-400',   border: 'border-amber-500/30',   bg: 'bg-amber-500/5' },
  grid_bot:     { label: 'Grid Bot',     icon: Grid3X3,   color: 'text-emerald-400', border: 'border-emerald-500/30', bg: 'bg-emerald-500/5' },
  dca_safety:   { label: 'DCA Safety',   icon: Shield,    color: 'text-violet-400',  border: 'border-violet-500/30',  bg: 'bg-violet-500/5' },
  cex:          { label: 'CEX Manual',   icon: Activity,  color: 'text-cyan-400',    border: 'border-cyan-500/30',    bg: 'bg-cyan-500/5' },
  dex:          { label: 'DEX Swap',     icon: Zap,       color: 'text-fuchsia-400', border: 'border-fuchsia-500/30', bg: 'bg-fuchsia-500/5' },
  futures:      { label: 'Futures',      icon: Layers,    color: 'text-orange-400',  border: 'border-orange-500/30',  bg: 'bg-orange-500/5' },
  algo_twap:    { label: 'Algo: TWAP',   icon: Clock,     color: 'text-blue-400',    border: 'border-blue-500/30',    bg: 'bg-blue-500/5' },
  algo_vwap:    { label: 'Algo: VWAP',   icon: Clock,     color: 'text-blue-400',    border: 'border-blue-500/30',    bg: 'bg-blue-500/5' },
  algo_iceberg: { label: 'Algo: Iceberg',icon: Layers,    color: 'text-blue-400',    border: 'border-blue-500/30',    bg: 'bg-blue-500/5' },
  algo_bracket: { label: 'Algo: Bracket',icon: Crosshair, color: 'text-blue-400',    border: 'border-blue-500/30',    bg: 'bg-blue-500/5' },
};

// Sparkline component for pairs trading z-score history
function ZScoreSparkline({ values, width = 80, height = 28 }) {
  if (!Array.isArray(values) || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);
  // Center line at z=0 (where spread = mean)
  const zeroY = height - ((0 - min) / range) * height;
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / range) * height).toFixed(1)}`)
    .join(' ');
  const lastZ = values[values.length - 1];
  const color = Math.abs(lastZ) > 2 ? '#fbbf24' : lastZ > 0 ? '#34d399' : '#f87171';
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" preserveAspectRatio="none">
      {/* z=0 reference line */}
      <line x1={0} y1={zeroY} x2={width} y2={zeroY} stroke="#475569" strokeWidth="0.5" strokeDasharray="2,2" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Dot at the most recent point */}
      <circle cx={(values.length - 1) * step} cy={height - ((lastZ - min) / range) * height} r="2" fill={color} />
    </svg>
  );
}

// Grouped data sources for the dropdown. CoinGecko is the reliable default;
// individual CEX exchanges are listed as alternatives for users who prefer
// price discovery from a specific venue.
const PAIRS_SOURCE_GROUPS = [
  {
    label: 'Aggregated (Recommended)',
    options: [
      { id: 'coingecko', label: 'CoinGecko (default)', description: 'Free public price aggregator · pulls from CEX + DEX globally · no exchange auth' },
    ],
  },
  {
    label: 'US-friendly exchanges',
    options: [
      { id: 'binanceus', label: 'Binance US',      description: 'US — broad USDT pair coverage' },
      { id: 'coinbase',  label: 'Coinbase',         description: 'US — USDC + USD pairs' },
      { id: 'kraken',    label: 'Kraken',           description: 'Global — USD primary' },
      { id: 'gemini',    label: 'Gemini',           description: 'US — USD primary, regulated' },
      { id: 'bitstamp',  label: 'Bitstamp',         description: 'EU + US — USD primary, oldest exchange' },
      { id: 'cryptocom', label: 'Crypto.com',       description: 'US-available — USDT + USD' },
    ],
  },
  {
    label: 'Global CEX exchanges',
    options: [
      { id: 'binance',   label: 'Binance (global)', description: 'Largest CEX — may be geo-blocked in US' },
      { id: 'bybit',     label: 'Bybit',            description: 'Derivatives + spot — USDT focus' },
      { id: 'okx',       label: 'OKX',              description: 'Global — USDT + USDC' },
      { id: 'kucoin',    label: 'KuCoin',           description: 'Global — USDT focus, wide alt coverage' },
      { id: 'gateio',    label: 'Gate.io',          description: 'Global — long-tail asset coverage' },
      { id: 'bitget',    label: 'Bitget',           description: 'Global — USDT + USDC' },
      { id: 'htx',       label: 'HTX (Huobi)',      description: 'Global — USDT' },
      { id: 'bitfinex',  label: 'Bitfinex',         description: 'Global — USD primary' },
      { id: 'poloniex',  label: 'Poloniex',         description: 'Global — USDT' },
    ],
  },
];

// Flat lookup for description rendering
const PAIRS_SOURCE_BY_ID = Object.fromEntries(
  PAIRS_SOURCE_GROUPS.flatMap(g => g.options).map(o => [o.id, o])
);

function PairsTradingTab({ pairs, loading, dataSource, onChangeDataSource, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const opps = Array.isArray(pairs?.opportunities) ? pairs.opportunities : [];
  const hasData = opps.length > 0;
  const hasError = !!pairs?.error;
  const cached = !!pairs?.cached;
  const symbolsUsed = pairs?.symbolsUsed || [];

  async function handleRefresh() {
    setRefreshing(true);
    try { await onRefresh?.(); }
    finally { setRefreshing(false); }
  }

  const isBusy = loading || refreshing;

  return (
    <div className="space-y-5">
      {/* Hero / explainer */}
      <div className="relative overflow-hidden bg-gradient-to-br from-cyan-500/10 via-violet-500/5 to-dark-800/40 rounded-2xl border border-cyan-500/20 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-violet-400/5 blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <GitBranch className="w-5 h-5 text-dark-900" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Pairs Trading Signals</h3>
              <p className="text-xs text-cyan-300/80 mt-0.5 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Mean-reversion of correlated-asset spreads · Real {pairs?.windowDays || 60}-day OHLCV (Binance US, Coinbase, Kraken with fallback)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs flex-wrap">
            <div className="bg-dark-900/40 backdrop-blur-sm rounded-lg border border-dark-700/40 px-3 py-2 text-center">
              <p className="text-[9px] uppercase tracking-wider text-gray-500">Symbols scanned</p>
              <p className="font-mono font-bold text-cyan-400">{symbolsUsed.length}</p>
            </div>
            <div className="bg-dark-900/40 backdrop-blur-sm rounded-lg border border-dark-700/40 px-3 py-2 text-center">
              <p className="text-[9px] uppercase tracking-wider text-gray-500">Pairs analyzed</p>
              <p className="font-mono font-bold text-violet-400">{pairs?.totalPairsScanned || 0}</p>
            </div>
            <div className="bg-dark-900/40 backdrop-blur-sm rounded-lg border border-dark-700/40 px-3 py-2 text-center">
              <p className="text-[9px] uppercase tracking-wider text-gray-500">Signals found</p>
              <p className="font-mono font-bold text-amber-400">{pairs?.qualifyingPairs || 0}</p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isBusy}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-cyan-400/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-50 text-xs"
              title="Force recompute (skip cache)"
            >
              <RefreshCw className={`w-3 h-3 ${isBusy ? 'animate-spin' : ''}`} />
              {isBusy ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Data source dropdown — grouped by category */}
        <div className="relative mt-4 pt-3 border-t border-dark-700/30 flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-gray-500">Data source</span>
          <select
            value={dataSource}
            onChange={(e) => onChangeDataSource?.(e.target.value)}
            disabled={isBusy}
            className="bg-dark-700 border border-dark-600 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-cyan-400/40 disabled:opacity-50 min-w-[180px]"
          >
            {PAIRS_SOURCE_GROUPS.map(group => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <p className="text-[10px] text-gray-500 flex-1 min-w-[200px]">
            {PAIRS_SOURCE_BY_ID[dataSource]?.description}
            {dataSource !== 'coingecko' && ' · Some assets may not be listed; expect fewer symbols.'}
          </p>
        </div>

        {/* Expandable explainer */}
        <div className="relative mt-4 pt-4 border-t border-dark-700/30">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[10px] uppercase tracking-wider font-bold text-cyan-400/80 hover:text-cyan-300 flex items-center gap-1"
          >
            <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            What is pairs trading? (Different from your ETH/USDC pair!)
          </button>
          {expanded && (
            <div className="mt-2 text-xs text-gray-300 leading-relaxed space-y-2">
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5 mb-2">
                <p className="text-amber-300 font-medium mb-1 flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" /> Terminology clarification
                </p>
                <p className="text-[11px] text-gray-300">
                  <strong className="text-white">"Trading pair"</strong> = one asset quoted against another (e.g. <code className="font-mono text-cyan-300">ETH/USDC</code> — your grid bot trades this).
                  <br/>
                  <strong className="text-white">"Pairs Trading"</strong> = a strategy that bets on the spread between <strong>two correlated assets</strong> (e.g. <code className="font-mono text-cyan-300">BTC vs ETH</code>). This page finds those opportunities.
                </p>
              </div>
              <p>
                <strong className="text-white">How it works</strong>: When two assets historically move together (correlation &gt; {pairs?.minCorrelation || 0.6}) and their price ratio strays more than ~2 standard deviations from its 60-day mean, the spread tends to revert. The trade: long the underperformer + sell the overperformer.
              </p>
              <p>
                <strong className="text-white">Z-score</strong> measures how stretched the spread currently is:
                <span className="text-amber-400 font-mono"> |z| ≥ 2</span> = statistically rare territory.
                <span className="text-emerald-400 font-mono"> |z| ≥ 2.5</span> = high conviction.
              </p>
              <p className="text-amber-300/80">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                These are statistical signals, not financial advice. Correlations break during regime shifts. Always size with risk controls.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Loading state — shown when actively fetching */}
      {isBusy && (
        <div className="bg-dark-800/40 rounded-2xl border border-cyan-500/20 p-10 text-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            <p className="text-white font-medium">Fetching market data…</p>
            <p className="text-xs text-gray-500 max-w-md">
              Pulling 60-day daily candles for 10 major assets from{' '}
              <span className="text-cyan-400 font-mono">{PAIRS_SOURCE_BY_ID[dataSource]?.label}</span>.
              This usually takes 5-10 seconds.
            </p>
          </div>
        </div>
      )}

      {/* Empty state — distinguish error vs honest "no signals" */}
      {!isBusy && !hasData && hasError && (
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-2xl p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-3">
            <AlertTriangle className="w-6 h-6 text-amber-400" />
          </div>
          <p className="text-amber-300 font-bold mb-1">Market data fetch failed</p>
          <p className="text-sm text-gray-400 max-w-md mx-auto mb-4 leading-relaxed">{pairs.error}</p>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-dark-900 font-semibold text-sm"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Retrying…' : 'Retry now'}
          </button>
          <p className="text-[10px] text-gray-600 mt-3">
            This usually clears within a minute. Force-refresh skips the cache and retries against Binance US → Coinbase → Kraken in order.
          </p>
        </div>
      )}
      {!isBusy && !hasData && !hasError && pairs && (
        <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-dark-700 flex items-center justify-center mx-auto mb-3">
            <GitBranch className="w-6 h-6 text-gray-500" />
          </div>
          <p className="text-white font-medium mb-1">No qualifying signals right now</p>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Scanned {pairs?.totalPairsScanned || 0} pairs across {symbolsUsed.length} symbols. None currently meet correlation ≥ {pairs?.minCorrelation || 0.6} AND |z-score| ≥ {pairs?.minAbsZScore || 1.5}.
            Markets are reverted or uncorrelated — these stretched-spread setups appear during divergences, not in calm regimes.
          </p>
        </div>
      )}

      {/* Signal cards */}
      {!isBusy && hasData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {opps.map((p, i) => {
            const conf = p.confidence;
            const confColor = conf === 'High' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
                            : conf === 'Medium' ? 'text-amber-400 bg-amber-500/10 border-amber-500/30'
                            : 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30';
            const zPositive = p.zScore > 0;
            return (
              <div key={i} className={`relative overflow-hidden bg-gradient-to-br from-dark-800/60 to-dark-800/40 rounded-2xl border border-dark-700/50 p-4 ${conf === 'High' ? 'ring-1 ring-emerald-400/20' : ''}`}>
                {/* Rank badge */}
                <div className="absolute top-3 right-3 text-[10px] text-gray-600 font-mono">#{i + 1}</div>

                {/* Header */}
                <div className="flex items-center gap-2 mb-3">
                  <GitBranch className={`w-4 h-4 ${conf === 'High' ? 'text-emerald-400' : 'text-cyan-400'}`} />
                  <span className="text-base font-bold text-white">{p.pair}</span>
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${confColor}`}>{conf.toUpperCase()}</span>
                  {p.userInvolved && (
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30" title="You already hold one of these legs">
                      YOURS
                    </span>
                  )}
                </div>

                {/* Z-score sparkline + key metrics */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <p className="text-[9px] uppercase tracking-wider text-gray-500">Z-score</p>
                    <p className={`text-xl font-bold font-mono ${Math.abs(p.zScore) >= 2 ? (zPositive ? 'text-emerald-400' : 'text-red-400') : 'text-amber-400'}`}>
                      {p.zScore >= 0 ? '+' : ''}{p.zScore.toFixed(2)}σ
                    </p>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase tracking-wider text-gray-500">Correlation</p>
                    <p className={`text-xl font-bold font-mono ${p.correlation >= 0.8 ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {p.correlation.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className="text-[9px] uppercase tracking-wider text-gray-500 mb-1">20-day spread (σ)</p>
                    <div className="flex-1 min-h-[28px]">
                      <ZScoreSparkline values={p.sparkline || []} />
                    </div>
                  </div>
                </div>

                {/* Signal */}
                <div className="bg-dark-900/40 rounded-lg p-3 border border-dark-700/40">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold flex items-center gap-1">
                    <Target className="w-3 h-3" /> Mean-reversion signal
                  </p>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm flex items-center gap-2 flex-wrap">
                      <span className={`font-bold px-2 py-0.5 rounded ${p.sideA === 'long' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                        {p.sideA === 'long' ? '↑ Long' : '↓ Short'} {p.symbolA.split('/')[0]}
                      </span>
                      <span className="text-gray-500 text-xs">+</span>
                      <span className={`font-bold px-2 py-0.5 rounded ${p.sideB === 'long' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                        {p.sideB === 'long' ? '↑ Long' : '↓ Short'} {p.symbolB.split('/')[0]}
                      </span>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
                    Spread is {Math.abs(p.zScore).toFixed(1)}σ {zPositive ? 'above' : 'below'} its 60-day mean.
                    {Math.abs(p.zScore) >= 2.5
                      ? ' Historically extreme — reversion typically takes 5-15 days.'
                      : Math.abs(p.zScore) >= 2.0
                      ? ' Statistically rare — watch for reversion signals.'
                      : ' Mild divergence — wait for confirmation or stronger signal.'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <p className="text-[10px] text-gray-600 text-center">
        {hasData
          ? `${opps.length} ${opps.length === 1 ? 'signal' : 'signals'} from real ${pairs?.windowDays || 60}-day OHLCV · `
          : ''}
        {cached ? 'Cached result' : 'Fresh computation'}
        {pairs?.computedAt ? ` · last refreshed ${formatTime(pairs.computedAt)}` : ''}
        {' · '}
        Not financial advice. Backtest before trading.
      </p>
    </div>
  );
}

function CorrelationEmptyState({ symbols }) {
  const haveOne = symbols.length === 1 ? symbols[0] : null;
  // Suggest popular complementary symbols based on what they already trade.
  // These suggestions are STATIC — every trader benefits from diversifying across them.
  const popularComplements = [
    { symbol: 'BTC',  description: 'Highest-cap, market beta benchmark' },
    { symbol: 'SOL',  description: 'High-beta L1 alternative' },
    { symbol: 'LINK', description: 'DeFi infrastructure exposure' },
    { symbol: 'ARB',  description: 'Layer-2 exposure' },
    { symbol: 'AVAX', description: 'Diversified L1' },
  ].filter(c => c.symbol !== haveOne);

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-blue-500/10 via-violet-500/5 to-dark-800/40 rounded-2xl border border-blue-500/20 p-8">
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-blue-400/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-violet-400/5 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-violet-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
            <Grid3X3 className="w-6 h-6 text-dark-900" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg mb-1">
              Correlation matrix needs at least 2 symbols
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed">
              You currently trade <span className="text-blue-300 font-mono font-bold">{haveOne || 'one symbol'}</span>.
              Correlation measures how positions move <strong>relative to each other</strong> — it needs at least
              2 different base assets to compare. Stablecoins (USDC, USDT) don't count as a separate position
              since they're cash legs.
            </p>
          </div>
        </div>

        {/* What you'd learn */}
        <div className="bg-dark-900/40 backdrop-blur-sm rounded-xl border border-dark-700/40 p-4 mb-5">
          <p className="text-[10px] uppercase tracking-wider text-blue-300 font-bold mb-2 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> What this unlocks
          </p>
          <ul className="space-y-1.5 text-xs text-gray-300">
            <li className="flex items-start gap-2">
              <ChevronRight className="w-3 h-3 text-blue-400 flex-shrink-0 mt-0.5" />
              <span><strong className="text-white">Diversification check</strong> — see if your positions are accidentally moving in lockstep (high correlation = no diversification)</span>
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight className="w-3 h-3 text-blue-400 flex-shrink-0 mt-0.5" />
              <span><strong className="text-white">Hedge opportunities</strong> — find negatively-correlated pairs that offset each other in downturns</span>
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight className="w-3 h-3 text-blue-400 flex-shrink-0 mt-0.5" />
              <span><strong className="text-white">Concentration risk</strong> — high all-pair correlation means your whole portfolio crashes together</span>
            </li>
          </ul>
        </div>

        {/* Suggestions */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Suggested symbols to trade alongside {haveOne || 'your current position'}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {popularComplements.map(c => (
              <div key={c.symbol} className="bg-dark-800/60 border border-dark-700/50 rounded-lg p-3">
                <p className="text-sm font-bold text-white font-mono">{c.symbol}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{c.description}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-gray-600 mt-3 leading-relaxed">
            Open any position in a different base asset (Smart Trade, Grid Bot, DCA Safety, or direct CEX/DEX trade) — once
            you have 2+ assets with realized fills, this matrix populates with real Pearson correlation coefficients
            from your actual P&L series.
          </p>
        </div>
      </div>
    </div>
  );
}

function PerSourceBreakdown({ bySource }) {
  // Filter out sources with zero meaningful activity (no closed trades AND no open positions)
  const sources = bySource.filter(s => s.lifetime.totalClosed > 0 || s.openPositionsCount > 0);
  if (sources.length === 0) return null;

  return (
    <div className="bg-gradient-to-br from-violet-500/5 to-blue-500/5 rounded-2xl border border-violet-500/20 p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-white font-bold flex items-center gap-2">
          <Layers className="w-4 h-4 text-violet-400" /> P&L by Trading Source
        </h3>
        <p className="text-[10px] text-violet-300/70 max-w-md text-right">
          Each strategy's cost-basis matches within itself — no cross-strategy FIFO contamination
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {sources.map(s => {
          const meta = SOURCE_META[s.source] || { label: s.source, icon: Activity, color: 'text-gray-400', border: 'border-gray-500/30', bg: 'bg-gray-500/5' };
          const Icon = meta.icon;
          const profit = s.lifetime.realizedPnL > 0;
          return (
            <div key={s.source} className={`rounded-xl border ${meta.border} ${meta.bg} p-4`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${meta.color}`} />
                  <span className={`text-sm font-bold ${meta.color}`}>{meta.label}</span>
                </div>
                <span className="text-[9px] text-gray-500 font-mono">
                  {s.assets.length} {s.assets.length === 1 ? 'asset' : 'assets'}
                </span>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Realized P&L</p>
                <p className={`text-2xl font-bold font-mono ${profit ? 'text-emerald-400' : s.lifetime.realizedPnL < 0 ? 'text-red-400' : 'text-white'}`}>
                  {s.lifetime.realizedPnL > 0 ? '+' : s.lifetime.realizedPnL < 0 ? '-' : ''}{formatUsd(Math.abs(s.lifetime.realizedPnL))}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-3 text-[10px]">
                <div>
                  <p className="text-gray-500">Win rate</p>
                  <p className={`font-mono font-bold ${s.lifetime.winRate >= 0.5 ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {(s.lifetime.winRate * 100).toFixed(0)}%
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Closed</p>
                  <p className="font-mono font-bold text-white">
                    {s.lifetime.winningTrades}W / {s.lifetime.losingTrades}L
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Open</p>
                  <p className="font-mono font-bold text-white">
                    {s.openPositionsCount}
                  </p>
                </div>
              </div>

              {s.lifetime.profitFactor != null && (
                <div className="mt-2 pt-2 border-t border-gray-700/30 text-[10px] flex justify-between">
                  <span className="text-gray-500">Profit factor</span>
                  <span className={`font-mono font-bold ${s.lifetime.profitFactor >= 1.5 ? 'text-emerald-400' : s.lifetime.profitFactor >= 1 ? 'text-amber-400' : 'text-red-400'}`}>
                    {s.lifetime.profitFactor.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function PortfolioAnalyticsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  // Time-window selector for the Overview tab. 7/30/60/90 days are quick chips;
  // 'custom' opens a date-range picker. The chosen window is sent to both
  // /risk-metrics and /ai-commentary so the whole tab stays in sync.
  const [overviewDays, setOverviewDays] = useState(90);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCustomRange, setShowCustomRange] = useState(false);
  // Refresh state for the Overview tab — we no longer auto-poll. Users
  // refresh manually via the Refresh button to get fresh data without
  // the page flashing every 5-10 seconds.
  const [overviewRefreshing, setOverviewRefreshing] = useState(false);
  const [overviewLastRefreshed, setOverviewLastRefreshed] = useState(null);

  // Beta gate for the Advanced Analytics feature
  const { feature: betaFeature, acknowledged, loading: betaLoading, markAcknowledged } = useBetaGate('advanced_analytics');
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    if (!betaLoading && betaFeature && !acknowledged && user?.role !== 'admin') setShowDisclaimer(true);
  }, [betaLoading, betaFeature, acknowledged, user]);

  // Data states
  const [riskMetrics, setRiskMetrics] = useState(null);
  const [correlation, setCorrelation] = useState(null);
  const [stressResult, setStressResult] = useState(null);
  const [stressScenario, setStressScenario] = useState('march_2020_covid');
  const [customBtcShock, setCustomBtcShock] = useState(-30);
  const [factors, setFactors] = useState(null);
  const [kelly, setKelly] = useState(null);
  const [aiCommentary, setAiCommentary] = useState(null);
  const [pairs, setPairs] = useState(null);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairsDataSource, setPairsDataSource] = useState('coingecko');
  const [whaleAlerts, setWhaleAlerts] = useState(null);
  const [smartMoney, setSmartMoney] = useState(null);
  const [accumulation, setAccumulation] = useState(null);
  const [whaleChain, setWhaleChain] = useState('ethereum');
  const [whaleMinUsd, setWhaleMinUsd] = useState(100000);
  const [whalesLoading, setWhalesLoading] = useState(false);
  const [yieldOpps, setYieldOpps] = useState(null);
  const [stressTesting, setStressTesting] = useState(false);

  useEffect(() => { loadOverview(); }, []);

  // NO auto-polling. The Overview tab loads once on mount; users refresh
  // manually via the Refresh button. Auto-polling was causing the page
  // to flash every 10 seconds with no clear feedback to the user.
  useEffect(() => {
    if (activeTab === 'correlation' && !correlation) loadCorrelation();
    if (activeTab === 'stress' && !stressResult) handleStressTest();
    if (activeTab === 'factors' && !factors) loadFactors();
    if (activeTab === 'kelly' && !kelly) loadKelly();
    if (activeTab === 'pairs' && !pairs) loadPairs();
    if (activeTab === 'whales' && !whaleAlerts) loadWhales();
    if (activeTab === 'yield' && !yieldOpps) loadYield();
  }, [activeTab]);

  async function loadOverview({ isManualRefresh = false, days, startDate, endDate } = {}) {
    // Initial load shows the full-page spinner. Manual refresh only shows
    // a small spinner on the Refresh button — the content stays mounted
    // so the page doesn't flash.
    if (!isManualRefresh) setLoading(true);
    else setOverviewRefreshing(true);
    try {
      const winDays = days ?? overviewDays;
      const args = startDate && endDate
        ? { startDate, endDate }
        : { days: winDays };
      const [rm, ai] = await Promise.all([
        getRiskMetrics(args),
        // AI commentary uses the same window as the risk metrics
        getAiCommentary({ days: args.days || 30 }),
      ]);
      setRiskMetrics(rm);
      setAiCommentary(ai);
      setOverviewLastRefreshed(Date.now());
    } catch (e) {
      console.error('[PortfolioAnalytics] overview load failed:', e);
    } finally {
      setLoading(false);
      setOverviewRefreshing(false);
    }
  }

  function selectWindow(days) {
    setOverviewDays(days);
    setShowCustomRange(false);
    loadOverview({ isManualRefresh: true, days });
  }

  function applyCustomRange() {
    if (!customStart || !customEnd) return;
    loadOverview({ isManualRefresh: true, startDate: customStart, endDate: customEnd });
  }

  async function loadCorrelation() { try { setCorrelation(await getCorrelationMatrix()); } catch (e) { console.error('[PortfolioAnalytics] correlation load failed:', e); } }
  async function loadFactors() { try { setFactors(await getFactorExposure()); } catch (e) { console.error('[PortfolioAnalytics] factors load failed:', e); } }
  async function loadKelly() { try { setKelly(await getKellyCriterion()); } catch (e) { console.error('[PortfolioAnalytics] kelly load failed:', e); } }
  async function loadPairs(force = false, source = pairsDataSource) {
    setPairsLoading(true);
    try {
      setPairs(await getPairsTradingOpportunities({ force, source }));
    } catch (e) {
      console.error('[PortfolioAnalytics] pairs load failed:', e);
    } finally {
      setPairsLoading(false);
    }
  }
  async function loadYield() { try { setYieldOpps(await getYieldOpportunities()); } catch (e) { console.error('[PortfolioAnalytics] yield load failed:', e); } }
  async function loadWhales(chain = whaleChain, minUsd = whaleMinUsd) {
    setWhalesLoading(true);
    try {
      // Smart-money threshold defaults to the same value as whale alerts but caller can adjust.
      const [alerts, sm, acc] = await Promise.all([
        getWhaleAlerts(chain, minUsd),
        getSmartMoneyWallets(chain, Math.max(minUsd, 250000)),
        getAccumulationTrends(chain, minUsd),
      ]);
      setWhaleAlerts(alerts);
      setSmartMoney(sm);
      setAccumulation(acc);
    } catch (e) {
      console.error('[PortfolioAnalytics] whales load failed:', e);
    } finally {
      setWhalesLoading(false);
    }
  }

  // Refetch whale data whenever the user changes chain or threshold.
  useEffect(() => {
    if (activeTab === 'whales') loadWhales(whaleChain, whaleMinUsd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whaleChain, whaleMinUsd]);

  async function handleStressTest() {
    setStressTesting(true);
    try {
      const payload = stressScenario === 'custom'
        ? { scenario: 'custom', btcShockPct: customBtcShock }
        : { scenario: stressScenario };
      setStressResult(await runStressTest(payload));
    } catch (e) { console.error('[PortfolioAnalytics] stress test failed:', e); }
    setStressTesting(false);
  }

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">

      {/* Beta disclaimer modal (first-time access) */}
      {showDisclaimer && betaFeature && (
        <FeatureDisclaimerModal
          feature={betaFeature}
          onAcknowledge={() => { markAcknowledged(); setShowDisclaimer(false); }}
          onCancel={() => { window.history.back(); }}
        />
      )}

      {/* Persistent disclaimer banner */}
      {betaFeature && acknowledged && <PersistentDisclaimerBanner feature={betaFeature} />}

      {/* Floating feedback button */}
      {betaFeature && acknowledged && <BetaFeedbackButton feature={betaFeature} />}

      {/* ── Premium Hero ── */}
      <div className="relative overflow-hidden rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-500/10 via-blue-500/5 to-dark-800/40 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-violet-400/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-blue-400/5 blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
              <BarChart2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-white">Portfolio Analytics</h1>
                {betaFeature && <BetaBadge feature={betaFeature} size="medium" />}
              </div>
              <p className="text-xs text-violet-300/80 mt-0.5 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Advanced risk metrics · Stress testing · Quant tools · Execution intelligence
              </p>
            </div>
          </div>
          {/* Live stat strip — only shown when we have real data */}
          {riskMetrics && !isLocked(riskMetrics) && (
            <div className="flex items-center gap-3 text-xs flex-wrap">
              <div className="bg-dark-900/40 backdrop-blur-sm rounded-lg border border-dark-700/40 px-3 py-2">
                <p className="text-[9px] uppercase tracking-wider text-gray-500">Portfolio</p>
                <p className="font-mono font-bold text-white">${(riskMetrics.totalValue || 0).toFixed(0)}</p>
              </div>
              <div className="bg-dark-900/40 backdrop-blur-sm rounded-lg border border-dark-700/40 px-3 py-2">
                <p className="text-[9px] uppercase tracking-wider text-gray-500">Trades used</p>
                <p className="font-mono font-bold text-violet-400">{riskMetrics.tradesUsed || 0}</p>
              </div>
              <div className="bg-dark-900/40 backdrop-blur-sm rounded-lg border border-dark-700/40 px-3 py-2">
                <p className="text-[9px] uppercase tracking-wider text-gray-500">Window</p>
                <p className="font-mono font-bold text-blue-400">{riskMetrics.period || '90 days'}</p>
              </div>
              {/* Window selector chips */}
              <div className="flex items-center gap-1 bg-dark-900/40 backdrop-blur-sm rounded-lg border border-dark-700/40 p-1">
                {[7, 30, 60, 90].map(d => (
                  <button
                    key={d}
                    onClick={() => selectWindow(d)}
                    disabled={overviewRefreshing}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider transition ${
                      !showCustomRange && overviewDays === d
                        ? 'bg-violet-500/30 text-violet-200 border border-violet-400/40'
                        : 'text-gray-400 hover:text-white hover:bg-dark-800/60'
                    }`}
                  >
                    {d}d
                  </button>
                ))}
                <button
                  onClick={() => setShowCustomRange(s => !s)}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider transition ${
                    showCustomRange
                      ? 'bg-violet-500/30 text-violet-200 border border-violet-400/40'
                      : 'text-gray-400 hover:text-white hover:bg-dark-800/60'
                  }`}
                >
                  Custom
                </button>
              </div>
              {/* Manual refresh button — replaces the old 10s auto-polling */}
              <button
                onClick={() => loadOverview({ isManualRefresh: true })}
                disabled={overviewRefreshing}
                title={overviewLastRefreshed ? `Last refreshed ${Math.floor((Date.now() - overviewLastRefreshed) / 1000)}s ago` : 'Refresh data'}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-violet-400/30 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 disabled:opacity-50 text-xs"
              >
                <RefreshCw className={`w-3 h-3 ${overviewRefreshing ? 'animate-spin' : ''}`} />
                {overviewRefreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          )}
          {riskMetrics && !isLocked(riskMetrics) && showCustomRange && (
            <div className="mt-3 flex items-center gap-2 bg-dark-900/40 backdrop-blur-sm rounded-lg border border-dark-700/40 px-3 py-2 text-xs">
              <span className="text-gray-400">From</span>
              <input
                type="date"
                value={customStart}
                onChange={e => setCustomStart(e.target.value)}
                className="bg-dark-800/60 border border-dark-700 rounded px-2 py-1 text-white font-mono"
              />
              <span className="text-gray-400">To</span>
              <input
                type="date"
                value={customEnd}
                onChange={e => setCustomEnd(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className="bg-dark-800/60 border border-dark-700 rounded px-2 py-1 text-white font-mono"
              />
              <button
                onClick={applyCustomRange}
                disabled={!customStart || !customEnd || overviewRefreshing}
                className="px-3 py-1 rounded-md bg-violet-500/20 border border-violet-400/40 text-violet-200 hover:bg-violet-500/30 disabled:opacity-40 text-[11px] font-semibold"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Data Source Banner ── */}
      {(() => {
        const activeData = activeTab === 'overview' ? riskMetrics : activeTab === 'stress' ? stressResult : activeTab === 'correlation' ? correlation : activeTab === 'factors' ? factors : activeTab === 'kelly' ? kelly : activeTab === 'pairs' ? pairs : activeTab === 'whales' ? accumulation : null;
        const isDemo = activeData?.dataSource === 'demo';
        const isReal = activeData?.dataSource === 'real';
        if (!activeData?.dataSource) return null;
        return (
          <div className={`rounded-xl px-4 py-2.5 border text-xs flex items-center gap-2 ${isDemo ? 'bg-amber-500/5 border-amber-500/20 text-amber-300' : 'bg-emerald-500/5 border-emerald-500/20 text-emerald-300'}`}>
            <span className="text-base">{isReal ? '✓' : 'ⓘ'}</span>
            <span>{activeData.dataSourceMessage}</span>
          </div>
        );
      })()}

      {/* ── Tabs (premium gradient on active) ── */}
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
        {TABS.map(tab => {
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                active
                  ? 'bg-gradient-to-r from-violet-500/20 to-blue-500/10 border border-violet-400/40 text-violet-300 shadow-lg shadow-violet-500/10'
                  : 'bg-dark-800/40 text-gray-500 border border-dark-700/50 hover:border-dark-600 hover:text-white'
              }`}>
              <tab.icon className="w-3.5 h-3.5" /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* ══════════════ RISK OVERVIEW ══════════════ */}
      {activeTab === 'overview' && isLocked(riskMetrics) && (
        <EmptyDemoCard
          icon={Shield}
          title="Risk metrics unlock with your real trades"
          description="Sharpe, Sortino, VaR, max drawdown, and Calmar are computed from your actual trade history. Need at least 5 trades to start."
          currentTrades={riskMetrics?.currentTrades}
          minRequired={riskMetrics?.minTradesRequired}
        />
      )}
      {activeTab === 'overview' && riskMetrics && !isLocked(riskMetrics) && (
        <div className="space-y-5">
          {/* AI Commentary */}
          {aiCommentary && (
            <div className="bg-gradient-to-br from-violet-500/5 to-blue-500/5 rounded-2xl border border-violet-500/20 p-5">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Brain className="w-4 h-4 text-violet-400" /> haythix AI Portfolio Commentary</h3>
              <div className="space-y-2 text-sm text-gray-300 leading-relaxed">
                <p>{aiCommentary.summary}</p>
                <p>{aiCommentary.performance}</p>
                <p>{aiCommentary.topMover}</p>
                <p>{aiCommentary.bottomMover}</p>
                <p className="text-amber-300">{aiCommentary.risk}</p>
                <p className="text-violet-300 font-medium">{aiCommentary.suggestion}</p>
              </div>
              <p className="text-[10px] text-gray-600 mt-3">Generated by haythix AI • {formatDateTime(aiCommentary.generatedAt)}</p>
            </div>
          )}

          {/* Risk metrics grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Sharpe Ratio" value={riskMetrics.sharpeRatio} icon={Gauge} color={parseFloat(riskMetrics.sharpeRatio) > 1 ? 'text-emerald-400' : parseFloat(riskMetrics.sharpeRatio) > 0.5 ? 'text-amber-400' : 'text-red-400'}
              benchmark="Good: >1.0" subtext="Return per unit of risk"
              info="Annualized return ÷ annualized total volatility. Good >1, excellent >2. Below 0.5 means your edge isn't covering risk. Assumes normal-distribution returns — crypto has fat tails, so treat as relative comparison across your own strategies." />
            <MetricCard label="Sortino Ratio" value={riskMetrics.sortinoRatio} icon={Shield} color={parseFloat(riskMetrics.sortinoRatio) > 1.5 ? 'text-emerald-400' : 'text-amber-400'}
              benchmark="Good: >1.5" subtext="Downside risk only"
              info="Like Sharpe but only penalizes DOWNSIDE volatility. Upside swings don't drag the number down. Crypto tends to look better on Sortino than Sharpe because of asymmetric distributions." />
            <MetricCard label="Max Drawdown" value={`-${riskMetrics.maxDrawdown}%`} icon={TrendingDown} color={parseFloat(riskMetrics.maxDrawdown) > 20 ? 'text-red-400' : 'text-amber-400'}
              benchmark="Low: <15%" subtext="Worst peak-to-trough"
              info="Largest peak-to-trough loss in the window. Low <15% is healthy; >25% is concerning; >50% means you survived a 2× recovery from bottom (rare). Recovery time matters as much as the depth." />
            <MetricCard label="Calmar Ratio" value={riskMetrics.calmarRatio} icon={Target} color={parseFloat(riskMetrics.calmarRatio) > 1 ? 'text-emerald-400' : 'text-amber-400'}
              benchmark="Good: >1.0" subtext="Return / max drawdown"
              info="Annualized return ÷ max drawdown. Penalizes deep drawdowns even if recovery happened. A strategy with 30% return but 50% drawdown (Calmar 0.6) is worse than one with 15% return + 10% drawdown (Calmar 1.5)." />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Value at Risk (95%)" value={formatUsd(parseInt(riskMetrics.valueAtRisk95))} icon={AlertTriangle} color="text-amber-400"
              subtext="Max daily loss (95% confidence)"
              info="On 95% of days, your portfolio shouldn't lose more than this. On 5% of days, it will. VaR is descriptive, not predictive — past distributions don't guarantee future ones." />
            <MetricCard label="Value at Risk (99%)" value={formatUsd(parseInt(riskMetrics.valueAtRisk99))} icon={AlertTriangle} color="text-red-400"
              subtext="Max daily loss (99% confidence)"
              info="On 99% of days you shouldn't lose more than this. The 1% of days you DO are when accounts blow up. Don't bet against this number — black swans live in the tails." />
            <MetricCard label="Win Rate" value={`${riskMetrics.winRate}%`} icon={TrendingUp} color="text-emerald-400"
              subtext={`Over ${riskMetrics.period}`}
              info="% of closed trades that ended in profit. 40-60% is normal for active trading. <40% is fine IF Profit Factor compensates (big winners vs many small losers)." />
            <MetricCard label="Profit Factor" value={riskMetrics.profitFactor} icon={DollarSign} color={parseFloat(riskMetrics.profitFactor) > 1.5 ? 'text-emerald-400' : 'text-amber-400'}
              benchmark="Good: >1.5" subtext="Gross profit / gross loss"
              info="Sum of all winning trades ÷ sum of all losing trades. 1.0 = break-even pre-fees. <1 = losing strategy. >1.5 = healthy. >3 = unusually good (verify the sample isn't too small)." />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <MetricCard label="Annualized Return" value={`${riskMetrics.annualizedReturn}%`} icon={TrendingUp} color={parseFloat(riskMetrics.annualizedReturn) > 0 ? 'text-emerald-400' : 'text-red-400'}
              info="Your realized return over the window, scaled to a full-year projection. Shorter samples produce noisier projections — 90+ days is the minimum honest sample." />
            <MetricCard label="Annualized Volatility" value={`${riskMetrics.annualizedVolatility}%`} icon={Activity} color={parseFloat(riskMetrics.annualizedVolatility) > 80 ? 'text-red-400' : 'text-amber-400'}
              info="Standard deviation of returns, annualized. >80% is high even for crypto. >150% means you're effectively running a high-leverage strategy whether or not you intended to." />
          </div>

          {/* Per-source breakdown — one card per trading strategy */}
          {Array.isArray(riskMetrics.bySource) && riskMetrics.bySource.length > 0 && (
            <PerSourceBreakdown bySource={riskMetrics.bySource} />
          )}
        </div>
      )}

      {/* ══════════════ STRESS TEST ══════════════ */}
      {activeTab === 'stress' && isLocked(stressResult) && (
        <EmptyDemoCard
          icon={AlertTriangle}
          title="Stress testing needs real positions"
          description="We apply historical crisis scenarios (March 2020, DeFi exploits, BTC -30%) to your actual holdings. Open a position to enable."
          currentTrades={stressResult?.currentTrades}
          minRequired={stressResult?.minTradesRequired}
        />
      )}
      {activeTab === 'stress' && stressResult && !isLocked(stressResult) && (
        <div className="space-y-5">
          <div className="relative overflow-hidden bg-gradient-to-br from-amber-500/5 to-dark-800/60 rounded-2xl border border-amber-500/20 p-5">
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-amber-400/5 blur-3xl pointer-events-none" />
            <div className="relative flex items-start justify-between flex-wrap gap-2 mb-4">
              <div>
                <h3 className="text-white font-bold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Portfolio Stress Test</h3>
                <p className="text-gray-400 text-xs mt-1">Replay a real historical crash or rally — your holdings get the same % change they actually experienced during that window. Or pick a custom BTC shock and we'll apply real 90-day betas.</p>
              </div>
              <span className="text-[10px] text-gray-500 font-mono">Price data: CoinGecko</span>
            </div>
            {/* Scenario picker */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
              {(stressResult?.availableScenarios || []).map(s => (
                <button
                  key={s.id}
                  onClick={() => { setStressScenario(s.id); }}
                  className={`text-left py-2.5 px-3 rounded-xl text-xs transition-all ${stressScenario === s.id ? 'bg-amber-400/15 text-amber-400 border border-amber-400/30' : 'bg-dark-700 text-gray-300 border border-dark-600 hover:border-amber-500/30'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{s.name}</span>
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${s.type === 'historical' ? 'bg-blue-500/20 text-blue-300' : 'bg-violet-500/20 text-violet-300'}`}>
                      {s.type === 'historical' ? 'Real' : 'Beta'}
                    </span>
                  </div>
                  {s.startDate && (
                    <div className="text-[10px] text-gray-500 mt-0.5 font-mono">{s.startDate} → {s.endDate}</div>
                  )}
                  {s.description && (
                    <div className="text-[10px] text-gray-500 mt-1">{s.description}</div>
                  )}
                </button>
              ))}
            </div>

            {/* Custom BTC shock input — only shown when 'custom' is selected */}
            {stressScenario === 'custom' && (
              <div className="flex items-center gap-2 bg-dark-900/40 rounded-lg border border-violet-500/20 px-3 py-2 mb-4 text-xs">
                <span className="text-gray-400">BTC change (%):</span>
                <input
                  type="number"
                  value={customBtcShock}
                  onChange={e => setCustomBtcShock(parseFloat(e.target.value) || 0)}
                  step="1"
                  min="-95"
                  max="500"
                  className="bg-dark-800/60 border border-dark-700 rounded px-2 py-1 text-white font-mono w-24"
                />
                <span className="text-[10px] text-gray-500">e.g. -30 = BTC drops 30%. We apply each of your holdings' real 90-day beta to derive its move.</span>
              </div>
            )}

            <button
              onClick={handleStressTest}
              disabled={stressTesting}
              className="px-6 py-2.5 rounded-xl text-sm font-bold bg-amber-500 text-dark-900 hover:bg-amber-400 disabled:opacity-40 flex items-center gap-2"
            >
              {stressTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Run Stress Test
            </button>
          </div>

          {stressResult && stressResult.positions && (
            <div className="relative overflow-hidden bg-gradient-to-br from-red-500/5 via-dark-800/60 to-dark-800/40 rounded-2xl border border-red-500/20 p-5">
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-red-400/5 blur-3xl pointer-events-none" />
              <div className="relative flex items-start justify-between flex-wrap gap-2 mb-3">
                <div>
                  <h3 className="text-white font-bold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> {stressResult.scenario}</h3>
                  {stressResult.startDate && (
                    <p className="text-[11px] text-gray-400 mt-0.5 font-mono">{stressResult.startDate} → {stressResult.endDate}</p>
                  )}
                  {stressResult.scenarioDescription && (
                    <p className="text-[11px] text-gray-500 mt-0.5">{stressResult.scenarioDescription}</p>
                  )}
                </div>
                {stressResult.scenarioType && (
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded ${stressResult.scenarioType === 'historical' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'bg-violet-500/20 text-violet-300 border border-violet-500/30'}`}>
                    {stressResult.scenarioType === 'historical' ? 'Real historical data' : 'Real 90-day beta'}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <MetricCard label="Current Value" value={formatUsd(stressResult.currentPortfolioValue)} color="text-white" />
                <MetricCard label="Stressed Value" value={formatUsd(stressResult.stressedPortfolioValue)} color={stressResult.totalChange >= 0 ? 'text-emerald-400' : 'text-red-400'} />
                <MetricCard label="Total Impact" value={`${stressResult.totalChangePct > 0 ? '+' : ''}${stressResult.totalChangePct}%`} color={stressResult.totalChange >= 0 ? 'text-emerald-400' : 'text-red-400'}
                  subtext={`${formatUsd(Math.abs(stressResult.totalChange))} ${stressResult.totalChange >= 0 ? 'gain' : 'loss'}`} />
              </div>
              <div className="space-y-2">
                {stressResult.positions.map(p => {
                  const isPositive = p.change >= 0;
                  const isUnavail = p.dataSource === 'unavailable';
                  return (
                    <div key={p.symbol} className="flex items-center gap-3 bg-dark-700/30 rounded-lg p-2.5">
                      <span className="text-white text-xs font-bold w-14">{p.symbol}</span>
                      <div className="flex-1 h-2 bg-dark-600 rounded-full overflow-hidden">
                        {isUnavail ? null : isPositive ? (
                          <div className="h-full bg-emerald-500/60 rounded-full" style={{ width: `${Math.min(100, p.changePct)}%` }} />
                        ) : (
                          <div className="h-full bg-red-500/60 rounded-full float-right" style={{ width: `${Math.min(100, Math.abs(p.changePct))}%` }} />
                        )}
                      </div>
                      {isUnavail ? (
                        <span className="text-[10px] text-gray-500 italic w-32 text-right">no CoinGecko data</span>
                      ) : (
                        <>
                          <span className={`text-xs font-mono font-bold w-16 text-right ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>{isPositive ? '+' : ''}{p.changePct.toFixed(1)}%</span>
                          <span className={`text-[10px] font-mono w-20 text-right ${isPositive ? 'text-emerald-400/60' : 'text-red-400/60'}`}>{isPositive ? '+' : ''}${p.change.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                          {p.beta != null && (
                            <span className="text-[10px] text-violet-300 font-mono w-14 text-right" title="90-day beta vs BTC">β {p.beta.toFixed(2)}</span>
                          )}
                          {p.startPrice != null && (
                            <span className="text-[10px] text-gray-500 font-mono w-32 text-right" title="Start → End price during the event">${p.startPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })} → ${p.endPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              {Array.isArray(stressResult.unavailable) && stressResult.unavailable.length > 0 && (
                <div className="mt-3 text-[10px] text-gray-500">
                  Excluded (no CoinGecko data): {stressResult.unavailable.join(', ')}
                </div>
              )}
              {stressResult.attribution && (
                <div className="mt-3 text-[10px] text-gray-500 italic border-t border-dark-700/40 pt-2">{stressResult.attribution}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ CORRELATION MATRIX ══════════════ */}
      {activeTab === 'correlation' && isLocked(correlation) && (
        <EmptyDemoCard
          icon={Grid3X3}
          title="Correlation matrix unlocks with multi-symbol trades"
          description="The matrix needs real per-symbol return series. Make trades across 2+ different symbols to see how your positions move relative to each other."
          currentTrades={correlation?.currentTrades}
          minRequired={correlation?.minTradesRequired}
        />
      )}
      {/* Empty state for users who only trade one symbol — explain what's
          needed AND suggest concrete next actions instead of rendering a
          meaningless 1x1 matrix with a 0.00 value. */}
      {activeTab === 'correlation' && correlation && !isLocked(correlation) && correlation.requiresMoreSymbols && (
        <CorrelationEmptyState symbols={correlation.symbols || []} />
      )}
      {activeTab === 'correlation' && correlation && !isLocked(correlation) && !correlation.requiresMoreSymbols && (
        <div className="space-y-5">
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-500/5 to-dark-800/60 rounded-2xl border border-blue-500/20 p-5">
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-blue-400/5 blur-3xl pointer-events-none" />
            <div className="relative flex items-start justify-between flex-wrap gap-2 mb-4">
              <div>
                <h3 className="text-white font-bold flex items-center gap-2"><Grid3X3 className="w-4 h-4 text-blue-400" /> Correlation Matrix · {correlation.period}</h3>
                <p className="text-gray-400 text-xs mt-1">Pair-wise Pearson correlation of daily price returns. High positive = portfolio crashes together. Negative = built-in hedge.</p>
              </div>
              <span className="text-[10px] text-gray-500 font-mono">Price data: Yahoo Finance</span>
            </div>

            {/* Color legend */}
            <div className="flex items-center gap-2 mb-3 text-[10px] font-mono">
              <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-300">+0.7 to +1.0 concentrated</span>
              <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300">+0.3 to +0.7 partial</span>
              <span className="px-2 py-0.5 rounded bg-dark-700/40 text-gray-400">−0.3 to +0.3 independent</span>
              <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">below −0.3 hedge</span>
            </div>

            <div className="overflow-x-auto relative">
              <table className="text-xs w-full">
                <thead>
                  <tr>
                    <th className="p-2 text-gray-500"></th>
                    {correlation.symbols?.map(s => <th key={s} className="p-2 text-gray-400 font-medium">{s}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {correlation.symbols?.map(s1 => (
                    <tr key={s1}>
                      <td className="p-2 text-gray-400 font-medium">{s1}</td>
                      {correlation.symbols.map(s2 => {
                        const v = s1 === s2 ? 1 : (correlation.matrix?.[s1]?.[s2] ?? 0);
                        // Color logic: HIGH POSITIVE correlation is BAD for diversification
                        // (they crash together). NEGATIVE correlation is GOOD (hedge).
                        let bg, fg;
                        if (s1 === s2) { bg = 'bg-violet-500/20'; fg = 'text-violet-300'; }
                        else if (v > 0.7) { bg = 'bg-red-500/20'; fg = 'text-red-300'; }
                        else if (v > 0.3) { bg = 'bg-amber-500/15'; fg = 'text-amber-300'; }
                        else if (v < -0.3) { bg = 'bg-emerald-500/20'; fg = 'text-emerald-300'; }
                        else { bg = 'bg-dark-700/30'; fg = 'text-gray-400'; }
                        const isStrong = Math.abs(v) > 0.7;
                        return (
                          <td key={`${s1}-${s2}`} className={`p-2 text-center font-mono ${bg} ${fg} ${isStrong ? 'font-bold' : ''}`}>
                            {v.toFixed(2)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {correlation.notable?.length > 0 && (
              <div className="mt-4 space-y-2 relative">
                <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">Insights</p>
                {correlation.notable.map((n, i) => {
                  const isWarning = n.type === 'high-correlation' || n.type === 'concentration';
                  return (
                    <div
                      key={i}
                      className={`flex items-start gap-2 text-xs p-3 rounded-lg border ${isWarning ? 'bg-amber-500/5 border-amber-500/20 text-amber-200' : 'bg-emerald-500/5 border-emerald-500/20 text-emerald-200'}`}
                    >
                      {isWarning
                        ? <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                        : <Shield className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />}
                      <span>{n.message}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {Array.isArray(correlation.unavailable) && correlation.unavailable.length > 0 && (
              <div className="mt-3 text-[10px] text-gray-500">
                Excluded (no Yahoo data): {correlation.unavailable.join(', ')}
              </div>
            )}

            {correlation.attribution && (
              <p className="mt-3 text-[10px] text-gray-500 italic border-t border-dark-700/40 pt-2">{correlation.attribution}</p>
            )}
          </div>
        </div>
      )}

      {/* ══════════════ FACTOR EXPOSURE ══════════════ */}
      {activeTab === 'factors' && isLocked(factors) && (
        <EmptyDemoCard
          icon={PieChart}
          title="Factor exposure needs your real holdings"
          description="We decompose your portfolio into Momentum / Volatility / DeFi / Layer-1/2 / Meme / Staking factors based on what you actually hold."
          currentTrades={factors?.currentTrades}
          minRequired={factors?.minTradesRequired}
        />
      )}
      {activeTab === 'factors' && factors && !isLocked(factors) && (
        <div className="space-y-5">
          <div className="relative overflow-hidden bg-gradient-to-br from-amber-500/5 to-dark-800/60 rounded-2xl border border-amber-500/20 p-5">
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-amber-400/5 blur-3xl pointer-events-none" />
            <div className="relative flex items-start justify-between flex-wrap gap-2 mb-4">
              <div>
                <h3 className="text-white font-bold flex items-center gap-2"><PieChart className="w-4 h-4 text-amber-400" /> Factor Exposure Analysis</h3>
                <p className="text-gray-400 text-xs mt-1">% of your portfolio in each crypto category. {factors.diversifiable ? 'Red = concentration risk if that sector reverses.' : 'Color-coded risk only meaningful with 3+ holdings.'}</p>
              </div>
              {factors.attribution && (
                <span className="text-[10px] text-gray-500 font-mono">{factors.attribution}</span>
              )}
            </div>

            {Array.isArray(factors.factors) && factors.factors.length > 0 ? (
              <div className="space-y-3">
                {factors.factors.map((f) => {
                  const pct = f.portfolioPct;
                  const { severity } = f;
                  const barColor = severity === 'high' ? 'bg-red-500' : severity === 'medium' ? 'bg-amber-500' : severity === 'low' ? 'bg-emerald-500' : 'bg-violet-500';
                  const textColor = severity === 'high' ? 'text-red-400' : severity === 'medium' ? 'text-amber-400' : severity === 'low' ? 'text-emerald-400' : 'text-violet-300';
                  return (
                    <div key={f.category}>
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-white text-xs font-medium truncate">{f.category}</span>
                          <span className="text-[10px] text-gray-500 whitespace-nowrap">· {f.holdingsCount} holding{f.holdingsCount === 1 ? '' : 's'}</span>
                        </div>
                        <span className={`text-xs font-mono font-bold whitespace-nowrap ${textColor}`}>{pct}%</span>
                      </div>
                      <div className="h-2.5 bg-dark-600 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic">No category data available for your current holdings.</p>
            )}

            {factors.warnings?.length > 0 && (
              <div className="mt-4 space-y-2">
                {factors.warnings.map((w, i) => (
                  <div key={i} className={`flex items-start gap-2 rounded-lg p-3 border ${factors.diversifiable ? 'bg-amber-500/5 border-amber-500/15' : 'bg-violet-500/5 border-violet-500/15'}`}>
                    <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${factors.diversifiable ? 'text-amber-400' : 'text-violet-400'}`} />
                    <p className={`text-xs ${factors.diversifiable ? 'text-amber-300' : 'text-violet-300'}`}>{w}</p>
                  </div>
                ))}
              </div>
            )}

            {Array.isArray(factors.unmapped) && factors.unmapped.length > 0 && (
              <div className="mt-4 bg-dark-900/40 rounded-lg p-3 border border-dark-700/40">
                <p className="text-[11px] text-gray-400 mb-1.5">CoinGecko has no category data for these holdings — excluded from analysis:</p>
                <div className="flex flex-wrap gap-1.5">
                  {factors.unmapped.map((u) => (
                    <span key={u.symbol} className="text-[10px] font-mono bg-dark-800/60 text-gray-300 px-2 py-0.5 rounded border border-dark-700/40">
                      {u.symbol} ({u.allocation}%)
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════ KELLY CRITERION ══════════════ */}
      {activeTab === 'kelly' && isLocked(kelly) && (
        <EmptyDemoCard
          icon={Calculator}
          title="Kelly Criterion needs realized P&L history"
          description="Optimal position sizing is computed from your win rate and average win/loss. Close at least 5 trades with realized P&L to unlock."
          currentTrades={kelly?.currentTrades}
          minRequired={kelly?.minTradesRequired}
        />
      )}
      {activeTab === 'kelly' && kelly && !isLocked(kelly) && kelly.insufficientClosedTrades && (
        <div className="rounded-2xl border border-violet-500/20 bg-violet-500/5 p-5 text-sm text-violet-200">
          <h3 className="font-bold text-white flex items-center gap-2 mb-1"><Calculator className="w-4 h-4 text-violet-400" /> Kelly Criterion</h3>
          <p>{kelly.message}</p>
        </div>
      )}
      {activeTab === 'kelly' && kelly && !isLocked(kelly) && !kelly.insufficientClosedTrades && (
        <div className="space-y-5">
          <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500/5 to-dark-800/60 rounded-2xl border border-emerald-500/20 p-5">
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-emerald-400/5 blur-3xl pointer-events-none" />
            <h3 className="relative text-white font-bold mb-2 flex items-center gap-2"><Calculator className="w-4 h-4 text-emerald-400" /> Kelly Criterion — Optimal Position Sizing</h3>
            <p className="text-gray-400 text-xs mb-4">The Kelly Criterion calculates the mathematically optimal fraction of your capital to risk per trade, based on your historical win rate and average win/loss. Inputs use each strategy's native accounting (matches your bot dashboards).</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <MetricCard label="Win Rate" value={`${kelly.winRate}%`} icon={TrendingUp} color="text-emerald-400"
                subtext={`${kelly.wins}W / ${kelly.losses}L`} />
              <MetricCard label="Avg Win" value={`${kelly.avgWin}%`} icon={ArrowUpRight} color="text-emerald-400"
                subtext={kelly.largestWinPct ? `largest +${kelly.largestWinPct}%` : undefined} />
              <MetricCard label="Avg Loss" value={`${kelly.avgLoss}%`} icon={ArrowDownRight} color="text-red-400"
                subtext={kelly.largestLossPct && parseFloat(kelly.largestLossPct) > 0 ? `largest -${kelly.largestLossPct}%` : undefined} />
              <MetricCard label="Win/Loss Ratio" value={kelly.winLossRatio != null ? `${kelly.winLossRatio}:1` : '—'} icon={Target} color="text-amber-400" />
            </div>

            {/* Kelly fraction cards — only shown when Kelly is mathematically defined */}
            {kelly.kellyStatus === 'computed' ? (
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className={`rounded-xl p-4 border text-center ${kelly.recommendation === 'fullKelly' ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-dark-700/30 border-dark-600/30'}`}>
                  <p className="text-gray-500 text-[10px]">Full Kelly</p>
                  <p className="text-2xl font-bold text-white font-mono">{kelly.fullKelly}%</p>
                  <p className="text-[10px] text-gray-600">Aggressive</p>
                </div>
                <div className={`rounded-xl p-4 border text-center ${kelly.recommendation === 'halfKelly' ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-dark-700/30 border-dark-600/30'}`}>
                  <p className="text-gray-500 text-[10px]">Half Kelly</p>
                  <p className="text-2xl font-bold text-amber-400 font-mono">{kelly.halfKelly}%</p>
                  <p className="text-[10px] text-gray-600">Recommended</p>
                  {kelly.recommendation === 'halfKelly' && <span className="text-[8px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 mt-1 inline-block">SUGGESTED</span>}
                </div>
                <div className={`rounded-xl p-4 border text-center ${kelly.recommendation === 'quarterKelly' ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-dark-700/30 border-dark-600/30'}`}>
                  <p className="text-gray-500 text-[10px]">Quarter Kelly</p>
                  <p className="text-2xl font-bold text-white font-mono">{kelly.quarterKelly}%</p>
                  <p className="text-[10px] text-gray-600">Conservative</p>
                </div>
              </div>
            ) : (
              <div className="rounded-xl p-4 border border-amber-500/20 bg-amber-500/5 mb-4">
                <div className="flex items-center justify-center gap-3 text-amber-300">
                  <AlertTriangle className="w-4 h-4" />
                  <p className="text-xs font-mono">
                    Kelly fraction is {kelly.kellyStatus === 'undefined-no-losses' ? 'undefined' : 'zero'} — no Full/Half/Quarter recommendation can be given from this sample.
                  </p>
                </div>
              </div>
            )}

            {/* Per-source trade breakdown */}
            {Array.isArray(kelly.sourceSummary) && kelly.sourceSummary.length > 0 && (
              <div className="bg-dark-900/30 rounded-xl p-3 border border-dark-700/40 mb-4">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Closed round-trips by source ({kelly.totalClosed} total over {kelly.period})</p>
                <div className="flex flex-wrap gap-2">
                  {kelly.sourceSummary.map(s => (
                    <div key={s.source} className="flex items-center gap-2 bg-dark-800/60 rounded-lg px-2.5 py-1 border border-dark-700/40">
                      <span className="text-[10px] font-mono uppercase text-gray-400">{s.source.replace('_', ' ')}</span>
                      <span className="text-[10px] font-mono text-emerald-400">{s.wins}W</span>
                      <span className="text-[10px] font-mono text-red-400">{s.losses}L</span>
                      <span className="text-[10px] font-mono text-gray-500">{(s.winRate * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-violet-500/5 rounded-xl p-4 border border-violet-500/15">
              <div className="flex items-start gap-2">
                <Brain className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-300">{kelly.explanation}</p>
              </div>
            </div>

            {/* Honest caveats — surface when Kelly is undefined / not appropriate */}
            {Array.isArray(kelly.notes) && kelly.notes.length > 0 && (
              <div className="mt-3 space-y-2">
                {kelly.notes.map((n, i) => (
                  <div key={i} className="flex items-start gap-2 bg-amber-500/5 rounded-lg p-3 border border-amber-500/15">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-200">{n}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════ PAIRS TRADING ══════════════ */}
      {activeTab === 'pairs' && (
        <PairsTradingTab
          pairs={pairs}
          loading={pairsLoading}
          dataSource={pairsDataSource}
          onChangeDataSource={(src) => { setPairsDataSource(src); loadPairs(false, src); }}
          onRefresh={() => loadPairs(true, pairsDataSource)}
        />
      )}

      {/* ══════════════ WHALE WATCHER ══════════════ */}
      {activeTab === 'whales' && (
        <div className="space-y-5">
          {/* Controls — chain + threshold dropdowns */}
          <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-500 uppercase tracking-wider">Chain</span>
              <select
                value={whaleChain}
                onChange={e => setWhaleChain(e.target.value)}
                disabled={whalesLoading}
                className="bg-dark-700 border border-dark-600 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-accent-primary disabled:opacity-50"
              >
                <option value="ethereum">Ethereum</option>
                <option value="polygon">Polygon</option>
                <option value="arbitrum">Arbitrum</option>
                <option value="base">Base</option>
                <option value="optimism">Optimism</option>
                <option value="avalanche">Avalanche</option>
                <option value="bsc">BNB Chain</option>
                <option value="solana">Solana</option>
                <option value="linea">Linea</option>
                <option value="scroll">Scroll</option>
                <option value="zksync">zkSync Era</option>
                <option value="blast">Blast</option>
                <option value="unichain">Unichain</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-500 uppercase tracking-wider">Whale threshold</span>
              <select
                value={whaleMinUsd}
                onChange={e => setWhaleMinUsd(parseInt(e.target.value, 10))}
                disabled={whalesLoading}
                className="bg-dark-700 border border-dark-600 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-accent-primary disabled:opacity-50"
              >
                <option value={100000}>$100k+</option>
                <option value={250000}>$250k+</option>
                <option value={500000}>$500k+</option>
                <option value={1000000}>$1M+</option>
                <option value={5000000}>$5M+</option>
                <option value={10000000}>$10M+</option>
              </select>
            </div>
            <button
              onClick={() => loadWhales(whaleChain, whaleMinUsd)}
              disabled={whalesLoading}
              className="ml-auto bg-dark-700 hover:bg-dark-600 border border-dark-600 rounded-lg px-3 py-1.5 text-xs text-gray-300 flex items-center gap-1.5 disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${whalesLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          {whalesLoading && !whaleAlerts && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-8 flex items-center justify-center text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Fetching on-chain data…
            </div>
          )}

          {/* Recent whale alerts — every row is a real on-chain tx */}
          {whaleAlerts && (
            <div className="relative overflow-hidden bg-gradient-to-br from-amber-500/5 to-dark-800/60 rounded-2xl border border-amber-500/20 p-5">
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-amber-400/5 blur-3xl pointer-events-none" />
              <div className="relative flex items-center justify-between mb-3">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <Activity className="w-4 h-4 text-amber-400" /> Whale Transactions
                </h3>
                <span className="text-[10px] text-gray-500">{whaleAlerts.message}</span>
              </div>
              {whaleAlerts.alerts?.length > 0 ? (
                <div className="space-y-2">
                  {whaleAlerts.alerts.map(a => (
                    <a
                      key={a.id}
                      href={`${({ethereum:'https://etherscan.io',polygon:'https://polygonscan.com',arbitrum:'https://arbiscan.io',base:'https://basescan.org',optimism:'https://optimistic.etherscan.io',avalanche:'https://snowtrace.io',bsc:'https://bscscan.com',solana:'https://solscan.io',linea:'https://lineascan.build',scroll:'https://scrollscan.com',zksync:'https://era.zksync.network',blast:'https://blastscan.io','polygon-zkevm':'https://zkevm.polygonscan.com',unichain:'https://uniscan.xyz'})[whaleChain]}/tx/${a.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 bg-dark-700/30 rounded-lg p-3 border border-dark-600/30 hover:border-dark-500 transition-colors"
                    >
                      <span className="text-lg">{a.type === 'buy' ? '🟢' : a.type === 'sell' ? '🔴' : '🔄'}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${a.type === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : a.type === 'sell' ? 'bg-red-500/15 text-red-400' : 'bg-gray-500/15 text-gray-400'}`}>
                            {a.type === 'buy' ? 'CEX OUTFLOW' : a.type === 'sell' ? 'CEX INFLOW' : 'TRANSFER'}
                          </span>
                          <span className="text-white text-xs font-bold">{a.asset}</span>
                          <span className="text-emerald-400 text-xs font-mono">${a.usdValue.toLocaleString()}</span>
                        </div>
                        <p className="text-gray-500 text-[10px] truncate">
                          <span className={a.fromType === 'cex' ? 'text-amber-400' : ''}>{a.from}</span>
                          <span className="mx-1">→</span>
                          <span className={a.toType === 'cex' ? 'text-amber-400' : ''}>{a.to}</span>
                        </p>
                      </div>
                      <ExternalLink className="w-3 h-3 text-gray-600 flex-shrink-0" />
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-xs text-center py-6">
                  No transactions above ${whaleMinUsd.toLocaleString()} on {whaleChain} in the recent window. Try lowering the threshold.
                </p>
              )}
            </div>
          )}

          {/* Accumulation vs distribution — derived from same transfer data */}
          {accumulation && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400" /> Accumulation vs Distribution
                </h3>
                <span className="text-[10px] text-gray-500">Net CEX flow per token · positive = whales withdrawing</span>
              </div>
              {accumulation.trends?.length > 0 ? (
                <div className="space-y-2">
                  {accumulation.trends.map(t => {
                    const isPos = t.netCexFlowUsd > 0;
                    const flowM = Math.abs(t.netCexFlowUsd) / 1e6;
                    const totalM = t.totalUsd / 1e6;
                    const signalText = t.signal.replace(/_/g, ' ');
                    const isStrong = t.signal.startsWith('strong_');
                    return (
                      <div key={t.asset} className="flex items-center gap-3 bg-dark-700/30 rounded-xl p-3 border border-dark-600/30">
                        <span className="text-white font-bold text-sm w-14">{t.asset}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${isPos ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                              {signalText}
                            </span>
                            {isStrong && <span className="text-[9px] font-bold text-amber-400">●</span>}
                          </div>
                          <p className="text-gray-500 text-[10px]">
                            Net CEX flow: <span className={isPos ? 'text-emerald-400' : 'text-red-400'}>{isPos ? '+' : '-'}${flowM.toFixed(2)}M</span>
                            <span className="mx-2">·</span>
                            Total volume: ${totalM.toFixed(2)}M
                            <span className="mx-2">·</span>
                            {t.transferCount} transfers
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-xs text-center py-6">
                  No tokens met the ${whaleMinUsd.toLocaleString()} threshold on {whaleChain} in the recent window.
                </p>
              )}
            </div>
          )}

          {/* Labeled-wallet activity (the honest version of "Smart Money") */}
          {smartMoney && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <Eye className="w-4 h-4 text-violet-400" /> Labeled-Wallet Activity
                </h3>
                <span className="text-[10px] text-gray-500">Publicly-tagged CEX hot wallets + foundation treasuries</span>
              </div>
              {smartMoney.wallets?.length > 0 ? (
                <div className="space-y-2">
                  {smartMoney.wallets.map(w => (
                    <div key={w.address} className="bg-dark-700/30 rounded-xl p-3 border border-dark-600/30">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-white text-xs font-bold">{w.label}</span>
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-400">{w.type}</span>
                        <span className="text-gray-500 text-[10px] font-mono ml-auto">{w.address.slice(0, 6)}…{w.address.slice(-4)}</span>
                        <span className="text-emerald-400 text-xs font-mono">${(w.totalUsd / 1e6).toFixed(2)}M</span>
                        <span className="text-gray-500 text-[10px]">{w.txCount} tx</span>
                      </div>
                      {w.recentTransfers?.length > 0 && (
                        <div className="space-y-1 pl-2 border-l border-dark-600">
                          {w.recentTransfers.slice(0, 3).map(rt => (
                            <a
                              key={rt.txHash}
                              href={`${({ethereum:'https://etherscan.io',polygon:'https://polygonscan.com',arbitrum:'https://arbiscan.io',base:'https://basescan.org',optimism:'https://optimistic.etherscan.io',avalanche:'https://snowtrace.io',bsc:'https://bscscan.com',solana:'https://solscan.io',linea:'https://lineascan.build',scroll:'https://scrollscan.com',zksync:'https://era.zksync.network',blast:'https://blastscan.io','polygon-zkevm':'https://zkevm.polygonscan.com',unichain:'https://uniscan.xyz'})[whaleChain]}/tx/${rt.txHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-[10px] text-gray-500 hover:text-gray-300 transition-colors"
                            >
                              {rt.direction === 'in' ? '←' : '→'} {rt.asset} · ${rt.usdValue.toLocaleString()}
                              <span className="ml-2 text-gray-700">{rt.timestamp ? new Date(rt.timestamp).toLocaleString() : ''}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-xs text-center py-6">
                  No labeled-wallet activity above ${Math.max(whaleMinUsd, 250000).toLocaleString()} on {whaleChain} in the recent window.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ YIELD OPPORTUNITIES ══════════════ */}
      {activeTab === 'yield' && yieldOpps && (
        <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500/5 to-dark-800/60 rounded-2xl border border-emerald-500/20 p-5">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-emerald-400/5 blur-3xl pointer-events-none" />
          <h3 className="relative text-white font-bold mb-2 flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-400" /> Yield on Idle Capital</h3>
          <p className="relative text-gray-400 text-xs mb-4">Put your uninvested stablecoins and ETH to work. These are current live rates from top DeFi protocols.</p>
          <div className="space-y-2">
            {yieldOpps.opportunities?.map((o, i) => (
              <div key={i} className="flex items-center gap-3 bg-dark-700/30 rounded-xl p-3 border border-dark-600/30">
                <div className="w-10 h-10 rounded-xl bg-dark-600 flex items-center justify-center text-xs font-bold text-white">{o.asset.slice(0, 3)}</div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">{o.asset} on {o.protocol}</p>
                  <p className="text-gray-500 text-[10px]">{o.chain} | TVL: {o.tvl} | {o.type || 'Lending'}</p>
                </div>
                <div className="text-right">
                  <p className="text-emerald-400 text-lg font-bold font-mono">{o.apy}%</p>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${o.risk === 'Low' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>{o.risk} Risk</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
